import * as React from "react"
import {
  CdnIntegration,
  CdnVendor,
  MutationResult,
} from "@modules/graphql/types"
import {
  HostingDetailsForSiteDocument,
  useConnectCloudCdnToSiteMutation,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import {
  SupportedCdnIntegrationsDocument,
  SupportedCdnIntegrationsQuery,
  SupportedCdnIntegrationsQueryVariables,
  useDisconnectCloudCdnToSiteMutation,
} from "@modules/cdn/shared/queries.generated"

import { DataProxy } from "apollo-cache"

export function updateCDNIntegrationCache(
  proxy: DataProxy,
  siteId: string,
  update: (cdnIntegration: CdnIntegration) => CdnIntegration
) {
  const queryInfo = {
    query: SupportedCdnIntegrationsDocument,
    variables: {
      siteId: siteId,
    },
  }

  const queryData = proxy.readQuery<
    SupportedCdnIntegrationsQuery,
    SupportedCdnIntegrationsQueryVariables
  >(queryInfo)

  proxy.writeQuery<
    SupportedCdnIntegrationsQuery,
    SupportedCdnIntegrationsQueryVariables
  >({
    ...queryInfo,
    data: {
      ...queryData,
      supportedCDNIntegrations: queryData?.supportedCDNIntegrations?.map(
        update
      ),
    },
  })
}

export function useConnectCloudCdn(
  siteId: string
): {
  isUpdatingCloudCdn: boolean
  connectCloudCdn: () => Promise<MutationResult | null | undefined>
  disconnectCloudCdn: () => Promise<MutationResult | null | undefined>
} {
  const [connectCloudCdnToSiteMutation] = useConnectCloudCdnToSiteMutation()
  const [disconnectCloudCdnMutation] = useDisconnectCloudCdnToSiteMutation()
  const [isUpdatingCloudCdn, setIsUpdatingCloudCdn] = React.useState(false)

  const connectCloudCdn = async () => {
    setIsUpdatingCloudCdn(true)
    const result = await connectCloudCdnToSiteMutation({
      variables: {
        siteId,
      },
      update: (proxy, { data }) => {
        if (!data?.connectCloudCdnToSite?.success) {
          return
        }

        updateCDNIntegrationCache(proxy, siteId, cdnIntegration => {
          if (
            cdnIntegration.vendor !== CdnVendor.CloudCdn &&
            cdnIntegration.connected === true
          ) {
            return { ...cdnIntegration, connected: false }
          }
          return cdnIntegration
        })
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
        {
          query: SupportedCdnIntegrationsDocument,
          variables: { siteId },
        },
      ],
    })
    setIsUpdatingCloudCdn(false)

    return result?.data?.connectCloudCdnToSite
  }

  const disconnectCloudCdn = async () => {
    setIsUpdatingCloudCdn(true)
    const result = await disconnectCloudCdnMutation({
      variables: {
        siteId,
      },
      update: (proxy, { data }) => {
        if (!data?.disconnectCloudCdnToSite?.success) {
          return
        }

        updateCDNIntegrationCache(proxy, siteId, cdnIntegration => {
          if (
            cdnIntegration.vendor === CdnVendor.CloudCdn &&
            cdnIntegration.connected === true
          ) {
            return { ...cdnIntegration, connected: false }
          }
          return cdnIntegration
        })
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
      ],
    })
    setIsUpdatingCloudCdn(false)

    return result?.data?.disconnectCloudCdnToSite
  }

  return {
    isUpdatingCloudCdn,
    connectCloudCdn,
    disconnectCloudCdn,
  }
}
